import { API_URL } from '../config/api';
import { tokenManager } from '../context/AuthContext';

// API client for employee Expense Vouchers (Phase 2).
// Ownership, status rules, validation and totals are enforced server-side.

export class ApiError extends Error {
  constructor(message, { status, errors, code } = {}) {
    super(message);
    this.status = status;
    this.errors = errors || {};
    this.code = code;
  }
}

const BASE = '/expenses/vouchers';

// Turns a non-2xx response into an ApiError (shared with the approval client)
export const parseResponse = async (response, forbiddenMessage) => {
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }
  if (!response.ok) {
    const message = response.status === 403 && forbiddenMessage
      ? forbiddenMessage
      : data?.message || data?.error || `Request failed (${response.status})`;
    throw new ApiError(message, { status: response.status, errors: data?.errors, code: data?.code });
  }
  return data;
};

const parse = (response) => parseResponse(response, 'You do not have access to Expense vouchers.');

// Bills are behind auth, so fetch as a blob and hand it to the browser
export const downloadBlob = async (url, filename) => {
  const response = await fetch(url, { headers: { ...tokenManager.getAuthHeader() } });
  if (!response.ok) await parseResponse(response);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename || 'bill';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const request = async (method, path, body) => parse(await fetch(`${API_URL}${path}`, {
  method,
  headers: { ...tokenManager.getAuthHeader(), 'Content-Type': 'application/json' },
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
}));

export const toQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
};

export const STATUS_META = {
  DRAFT: { label: 'Draft', color: 'default' },
  SENT_BACK: { label: 'Sent Back', color: 'warning' },
  PENDING_ACCOUNTS: { label: 'Pending Accounts', color: 'info' },
  PENDING_APPROVAL: { label: 'Pending Approval', color: 'secondary' },
  APPROVED: { label: 'Approved', color: 'success' },
  REJECTED: { label: 'Rejected', color: 'error' },
};

export const vouchersApi = {
  list: (params) => request('GET', `${BASE}${toQuery(params)}`),
  formContext: (voucherId) => request('GET', `${BASE}/form-context${toQuery({ voucherId })}`),
  get: (id) => request('GET', `${BASE}/${id}`),
  create: (body) => request('POST', BASE, body),
  update: (id, body) => request('PUT', `${BASE}/${id}`, body),
  remove: (id) => request('DELETE', `${BASE}/${id}`),
  submit: (id, version) => request('POST', `${BASE}/${id}/submit`, { version }),
  // Settlement draft for an approved advance (returns the existing draft if any)
  createSettlement: (id) => request('POST', `${BASE}/${id}/settlement`),

  uploadBills: async (id, lineId, files) => {
    const form = new FormData();
    Array.from(files).forEach((f) => form.append('files', f));
    return parse(await fetch(`${API_URL}${BASE}/${id}/lines/${lineId}/bills`, {
      method: 'POST',
      headers: { ...tokenManager.getAuthHeader() },
      body: form,
    }));
  },

  deleteBill: (id, billId) => request('DELETE', `${BASE}/${id}/bills/${billId}`),

  downloadBill: (id, bill) => downloadBlob(`${API_URL}${BASE}/${id}/bills/${bill._id}`, bill.originalName),
};

export const formatAmount = (amount, currency) => {
  if (typeof amount !== 'number') return '-';
  const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
  return currency ? `${currency} ${formatted}` : formatted;
};

// "INR 1,200 + IDR 50,000" (no conversion between currencies)
export const formatTotals = (voucher) => {
  const totals = voucher?.totalsByCurrency || [];
  if (!totals.length) return formatAmount(voucher?.totalAmount ?? 0);
  return totals.map((t) => formatAmount(t.amount, t.currency)).join(' + ');
};

export const formatDate = (value) => (value
  ? new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
  : '-');

export const formatDateTime = (value) => (value
  ? new Date(value).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '-');
